import { resolveAttackComposite, type AttackWeaponReaders } from './attack-weapon-resolution';
import { mergeWeaponInventoryAttackLayers } from '../compile/inventory-ability-grant-helpers';
import { THROW_HAND_SLOTS } from './throw-object-gate';
import { abilityChargeEntry } from '../ability/charges/charge-entry';
import { preflightPostRollChargeResources, janusPostRollChangeAbilityResources } from '../ability/charges/adjust-charges';
import { checkAbilitySizeCompatibility } from '../ability/check-ability-size-compatibility';
import { ecId, handBack, itemField, type JanusCtx, type JanusReaders } from '../ability/context';
import type { JanusInput } from '../ability/janus';
import { janusDispatchRollTypes } from '../ability/rolls/janus-dispatch-roll-types';
import { postEffectConditions } from '../ability/post-effect-conditions';
import { CASTER_MOVEMENT_GRANT_LAYERS } from '../ability/movement/apply-change-speed-remaining-layers-to-caster';
import { attackGrantsTeleportChoice } from '../ability/movement/ability-teleport';
import { effectTargetProceeds } from '../ability/is-target-valid-for-effect';
import { extraFragmentType } from '../ability/rolls/damage-type-roll';
import { type RollSource } from './roll-source';
import { createSkillRequirementChecker } from '../compile/skill-requirements';
import { statusLiveLayer } from '../compile/possessor-compile';
import { rowLayerNames } from '../compile/row-layer-names';
import { weaponAttackSkill } from './weapon-attack-skill';
import { refuseUnported } from '../unported';
import { EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL, FRAGMENT_KNOWN_LAYERS, layerListEntryUuid } from './weapon-attack-extra-damage';
import { TRIGGER_CARRIERS, passiveActive, predicateMatch } from '../reactions/trigger-dispatch';
import { isAttackPassiveWindow } from '../compile/status-attack-budget';
import { weaponAttackRollType, WEAPON_MELEE_ATTACK_ROLL, WEAPON_RANGED_ATTACK_ROLL, type WeaponAttackReaders, type WeaponAttackPartner } from './weapon-attack';
import { findWeaponAmmo } from './weapon-attack-inventory';
import { entryCharges } from './ability-preconditions';
import { chargeReductionDelta, type ReductionReaders } from '../resources/reduction-guard';
import { resourceReductionReaders } from '../ability/resource-reduction';
export { weaponAttackSkill } from './weapon-attack-skill';

type Row = Record<string, unknown>;
export interface ExplicitWeaponReaders extends AttackWeaponReaders {
    /** Arming may defer cold status payloads to the confirmed attack's hydrator. */
    deferStatusDefinitions?: boolean;
    creatureField?: (row: Row, key: string) => unknown;
    itemField: (row: Row | null, key: string) => unknown;
    computedValue: (row: Row, cluster: string, key: string) => unknown;
    skillEntries?: readonly { item_id: string; variable_name: string }[];
}
export type ExplicitWeaponBinding = { weapon: Row; ability: Row; skill: string } | { reason: string };
export const plainWeaponAbility = (id: string): Row => ({ items: { item_id: id, item_type: 'attack_ability' }, item_layer_types: {} });
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
/** The caster's stored target statuses: the ONE status reader the weapon lane's eligibility check, its charge-context readers and
 * the spend that follows (resourceReductionReaders over that context) all share. */
const weaponStatusRows = (row: Row): Row[] => { const list = record(row.statuses)?.target; return Array.isArray(list) ? list as Row[] : []; };
function weaponReductionReaders(read: ExplicitWeaponReaders): ReductionReaders {
    return { value: read.value, entries: read.entries, definition: read.resolveItem, statuses: weaponStatusRows };
}
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** Bind the exact inventory instance, never the resolver's automatic alternate weapon.
 * Damage/properties remain on the weapon; only the native grant helper's selected
 * layers propagate onto the attack context. Both source rows stay immutable. */
export function bindExplicitWeaponAttack(ability: Row, caster: Row, inventoryId: string, read: ExplicitWeaponReaders, throwing = false): ExplicitWeaponBinding {
    const inventory = read.entries(caster, 'inventory').map(record).find(row => row && upper(read.inventoryField(row, 'inventory_id')) === upper(inventoryId));
    if (!inventory) return { reason: 'weapon:inventory-instance-unavailable' };
    if (Number(read.inventoryField(inventory, 'inventory_deleted') ?? 0) !== 0
        || Number(read.inventoryField(inventory, 'inventory_private') ?? 0) === 2) return { reason: 'weapon:inventory-instance-unavailable' };
    const resolved = resolveAttackComposite({ ability, caster, weaponInventoryId: String(read.inventoryField(inventory, 'inventory_id') ?? '') }, read);
    if (!resolved || 'unknown' in resolved) return { reason: 'weapon:' + (resolved?.unknown ?? 'attack-ability-required') };
    if (upper(read.inventoryField(resolved.composite, 'inventory_id')) !== upper(inventoryId)) return { reason: 'weapon:explicit-binding-mismatch' };
    const merged = mergeWeaponInventoryAttackLayers(ability, inventory, {
        itemField: (row, key) => read.itemField(row as Row | null, key),
        layerValue: (row, key) => read.value(row as Row | null, key),
        layerEntries: (row, key) => read.entries(row as Row | null, key),
        resolveCarrierRef: id => read.resolveItem?.(id) ?? null,
    }) as Row;
    const enchanted = weaponEnchantmentAttackLayers(resolved.composite, read);
    if ('reason' in enchanted) return enchanted;
    const weapon = { ...enchanted.weapon, attack_ability_arr: merged };
    const skill = weaponAttackSkill(weapon, caster, read, throwing);
    return { weapon, ability: merged, skill };
}

/** Share the held other-hand selection with pre-draw definition hydration. */
export function weaponAttackPartnerSource(weapon: Row, caster: Row, read: ExplicitWeaponReaders,
    paidHand?: 'main' | 'off'): { partner: Row; held: Row | null | undefined; naturalId: string; other: 'main' | 'off' } | { reason: string } | null {
    const no = { reason: 'hand-slot-rider-unresolved' };
    const slot = paidHand ? THROW_HAND_SLOTS[`${paidHand}_hand`] : upper(read.inventoryField(weapon, 'equipped'));
    if ((slot !== THROW_HAND_SLOTS.main_hand && slot !== THROW_HAND_SLOTS.off_hand)
        || Number(read.value(caster, 'count_both_hands_weapon_equipped') ?? 0) > 0) return null;
    const other = slot === THROW_HAND_SLOTS.main_hand ? 'off' : 'main';
    const otherSlot = other === 'off' ? THROW_HAND_SLOTS.off_hand : THROW_HAND_SLOTS.main_hand;
    const held = read.entries(caster, 'inventory').map(record).find(row => row
        && upper(read.inventoryField(row, 'equipped')) === otherSlot
        && Number(read.inventoryField(row, 'inventory_deleted') ?? 0) === 0
        && Number(read.inventoryField(row, 'inventory_private') ?? 0) !== 2);
    // A shield/torch occupies the hand and suppresses its natural weapon too.
    if (held) {
        const type = read.itemType(held);
        if (!type) return no;
        if (type !== 'weapon') return null;
    }
    const naturalId = layerListEntryUuid(read.value(caster, `equipped_${other}_hand_natural_weapon_item_id`));
    const partner = held ?? (naturalId ? read.resolveItem?.(naturalId) : null);
    if (!partner) return naturalId || Number(read.value(caster, `count_${other}_hand_weapon_equipped`) ?? 0) > 0
        || Number(read.value(caster, `count_${other}_hand_natural_weapon_equipped`) ?? 0) > 0 ? no : null;
    return { partner, held, naturalId, other };
}

/** The shared weapon executor owns the partner's free swing, luck, damage and terminal settlement. */
export function planWeaponAttackPartner(weapon: Row, caster: Row, target: Row, read: ExplicitWeaponReaders,
    spatial: Pick<WeaponAttackReaders, 'distanceFt' | 'reachFt'>, paidHand?: 'main' | 'off'): WeaponAttackPartner | { reason: string } | null {
    if (Number(read.creatureField?.(target, 'encounter_creature_dead') ?? 0) !== 0) return null;
    const source = weaponAttackPartnerSource(weapon, caster, read, paidHand);
    if (!source || 'reason' in source) return source;
    let { partner } = source;
    const { held, naturalId, other } = source, no = { reason: 'hand-slot-rider-unresolved' };
    const rollType = weaponAttackRollType({}, partner, read);
    const distance = spatial.distanceFt(caster, target);
    if (!Number.isFinite(distance)) return no;
    if (rollType === WEAPON_MELEE_ATTACK_ROLL) {
        const weaponReach = Number(read.value(partner, 'weapon_reach') ?? 0);
        const reach = spatial.reachFt(caster) + Math.max(0, weaponReach - 5);
        if (!Number.isFinite(weaponReach) || weaponReach < 0 || !Number.isFinite(reach)) return no;
        if (distance > reach) return null;
    }
    if (rollType === WEAPON_RANGED_ATTACK_ROLL) {
        if (read.entries(partner, 'uses_ammo_classification').length && !findWeaponAmmo(caster, partner, read)) return null;
        const multiplier = Number(read.value(caster, 'weapon_ranged_attack_range_multiplier') ?? 0);
        const maximum = Number(read.value(partner, 'range_end')) * (multiplier > 1 ? multiplier : 1);
        if (!Number.isFinite(maximum) || maximum <= 0) return no;
        if (distance > maximum) return null;
    } else if (rollType !== WEAPON_MELEE_ATTACK_ROLL) return { reason: 'rider-roll-type-out-of-scope' };
    if (held) {
        const id = String(read.inventoryField(held, 'inventory_id') ?? '');
        const binding = bindExplicitWeaponAttack(plainWeaponAbility(id), caster, id, read);
        if ('reason' in binding) return binding;
        const reason = explicitWeaponFamilyRefusal(binding, caster, read);
        if (reason) return { reason };
        partner = binding.weapon;
    }
    return { weaponId: held ? String(read.inventoryField(held, 'inventory_id') ?? '') : naturalId,
        weapon: partner, label: other === 'off' ? 'off-hand' : 'main-hand', explicitInventoryWeapon: !!held };
}

/** An attached natural attack keeps its catalog identity and shared weapon skill.
 * Resolve its attachment through the same weapon selector, without an inventory instance. */
export function bindNaturalWeaponAttack(ability: Row, caster: Row, weaponId: string, read: ExplicitWeaponReaders): ExplicitWeaponBinding {
    const natural = read.resolveItem?.(upper(weaponId));
    if (!natural || read.itemType(natural) !== 'natural_weapon')
        return { reason: 'weapon:natural-ranged-binding-mismatch' };
    // The selector's head counter is melee economy; it cannot veto this ranged binding.
    const selectorCaster = upper(read.value(natural, 'roll_type')) === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C'
        ? { ...caster, encounter_creatures: { ...record(caster.encounter_creatures), head_attack: 0 } } : caster;
    const resolved = resolveAttackComposite({ ability, caster: selectorCaster, weaponInventoryId: weaponId }, read);
    if (!resolved || 'unknown' in resolved) return { reason: 'weapon:' + (resolved?.unknown ?? 'attack-ability-required') };
    const weapon = resolved.composite;
    if (read.itemType(weapon) !== 'natural_weapon' || upper(read.itemField(weapon, 'item_id')) !== upper(weaponId)
        || upper(read.value(weapon, 'roll_type')) !== upper(read.value(natural, 'roll_type')))
        return { reason: 'weapon:natural-ranged-binding-mismatch' };
    return { weapon, ability, skill: weaponAttackSkill(weapon, caster, read) };
}

/** Missing authored throw range is a rules refusal, not an unfinished browser capability. */
export const WEAPON_CANNOT_BE_THROWN = 'weapon:cannot-be-thrown';
export function reportWeaponAttackRefusal(reason: string, detail?: Record<string, unknown>): void {
    if (reason === WEAPON_CANNOT_BE_THROWN) window.displayWarning?.("This weapon can't be thrown");
    else refuseUnported(reason, detail);
}

// Context is consumed by the player precondition gate, binding and economy; hit effects use the ordinary ability
// dispatcher inside the attack's existing synthetic/live consequence frame.
const ATTACK_CONTEXT = new Set([
    'range', 'cost_gp', 'unenchantable', 'requires_level', 'requires_class', 'requires_class_level', 'requires_subclass',
    'attached_equipped_weapon', 'attached_weapon_form', 'if_weapon_classification_equipped_active', 'if_weapon_type_equipped_active',
    'requires_in_combat', 'effect_only_affects_opposing_team_type', 'effect_only_affects_same_team_type',
    'if_caster_has_status_effect_active',
    'max_targets', 'ignore_long_range_attack_roll_dis',
    'ability_charges', 'recharge_range_begin', 'recharge_range_end',
    'attack_ability_throws_the_throwing_weapon_at_target',
    'ability_only_works_against_size_category_number_above', 'ability_only_works_against_size_category_number_below',
]);
const HIT_EFFECTS = new Set([
    ...CASTER_MOVEMENT_GRANT_LAYERS,
    'skill_roll_contester', 'skill_roll_contestee', 'ability_dc',
    'status_effect_applied_to_target_after_contesting_roll_failed',
    'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_caster_on_hit',
    'duration_int', 'duration_type', 'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn',
    'effect_ends_on_target_start_of_turn', 'effect_ends_on_target_end_of_turn', 'stacks_applied',
    'roll_type_dis', 'roll_type_against_adv', 'target_marked_by_caster', 'triggers_on_next_roll_type',
    'push_ft_after_hit', 'pull_ft_after_hit', 'pull_to_caster_center',
    'dice', 'die', 'damage_type',
    'effect_ignores_damage_type_resistance',
    'consumes_ability_charges', 'consumes_charges_qty',
    'target_retries_contesting_roll_on_end_of_turn',
    'add_charges_to_ability', 'add_charges_qty',
]);
const CONTEST_ROLL = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const HIT_ROLLS = new Set([CONTEST_ROLL, '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0']);

/** A bound ability's lifetime marker has no derived payload: its context and
 * status-application instructions run on the hit, not again while it is held. */
export function explicitWeaponStatusIsRuntimeOnly(ability: Row): boolean {
    const layers = record(ability.item_layer_types);
    return itemField(ability, 'item_type') === 'attack_ability' && !!layers && Object.keys(layers).every(key =>
        ATTACK_CONTEXT.has(key) || HIT_EFFECTS.has(key) || key === 'roll_type' || key.startsWith('requires_skill_tier_'));
}

/** No second contest, status, duration or log implementation in the bound lane.
 * A miss never calls this; action costs remain the weapon lane's single debit. */
export function applyExplicitWeaponHitEffects(ctx: JanusCtx, ability: Row, caster: Row, target: Row,
    modifiers: Pick<JanusInput, 'rawDiceMod' | 'rawDiceAdvDisStacks'>): void {
    if (!ownLayerKeys(ability).some(key => HIT_EFFECTS.has(key) || key === 'roll_type')) return;
    const input: JanusInput = { casterId: ecId(caster), targetIds: [ecId(target)], abilityId: String(itemField(ability, 'item_id') ?? ''),
        ...modifiers, useAbilityVariable1: null, useAbilityVariable2: null };
    // Untyped riders share damage_type_roll's main-weapon / Bludgeoning fallback.
    // Keep the authored definition intact; provenance still names the applier.
    if (HIT_ROLLS.has(upper(ctx.read.value(ability, 'roll_type'))) && !ctx.read.has(ability, 'damage_type') && ctx.read.has(ability, 'dice'))
        ability = { ...ability, item_layer_types: { ...record(ability.item_layer_types), damage_type: [extraFragmentType(ctx, ability, caster).raw] } };
    if (ctx.read.has(ability, 'roll_type')) janusDispatchRollTypes(ctx, input, ability, caster, [target]);
    else if (effectTargetProceeds(ctx, input, ability, caster, target, true)) postEffectConditions(ctx, input, ability, caster, target);
}
const WEAPON_CONTEXT = new Set([
    'dice', 'die', 'damage_type', 'roll_type', 'weapon_reach', 'weapon_type', 'weapon_classification', 'weapon_category', 'equipment_slot', 'natural_weapon_form',
    'weapon_two_handed', 'item_throwable', 'range_begin', 'range_end', 'uses_ammo_classification', 'ignore_long_range_attack_roll_dis',
    'cost_gp', 'weight', 'size', 'item_composition', 'equipment_visual_mount', 'enchantment_template_applied',
    'attack_ability', 'opportunity_attack_ability', 'ability', 'quick_ability', 'reaction_ability', 'full_ability', 'charged_spell', 'charged_ability',
    'innate_spell_per_long_rest', 'at_will_spell', 'free_ability', 'unenchantable',
    'passive_ability', 'ac_mod_magical', 'weapon_heavy', 'weapon_light', 'weapon_finesse', 'versatile_dice', 'versatile_die',
    'weapon_attack_mod', 'ignore_damage_type_resistance', 'damage_heals_caster', 'effect_ignores_damage_type_resistance',
    'item_charges', 'current_item_charges', 'item_rechargeable', 'ranged_loading',
    // Equipment compiler inputs and wielding metadata; they do not add an attack-time duty.
    'speed_walking', 'skill_roll_dis', 'requires_min_essence_body',
]);
const COMPILED_ENCHANTMENT = new Set(['cost_gp', 'weight_multiplier', 'cost_gp_multiplier', 'ac_mod_magical',
    'inventory_body', 'inventory_mind', 'inventory_soul', 'inventory_spirit', 'inventory_fortune',
    'enchantment_item_type', 'enchantment_weapon_type', 'item_composition', 'bright_light_dim_light',
    'immune_status_effect', 'damage_type_immunity', 'contesting_roll_adv_vs_status_effect', 'skill_roll_adv',
    'attack_ability', 'opportunity_attack_ability', 'quick_ability', 'free_ability']);
const WEAPON_PASSIVE = new Set([...COMPILED_ENCHANTMENT, 'unenchantable', 'requires_level', 'requires_class',
    'requires_class_level', 'requires_subclass', 'ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach',
    'triggers_when_caster_makes_roll_type', 'triggers_on_hit', 'triggers_on_miss', 'triggers_on_critical_hit',
    'triggers_on_not_a_critical_hit', 'triggers_on_kill', 'change_speed_walking_remaining',
    'effect_ends_on_target_end_of_turn', 'ability_charges', 'recharge_range_begin', 'recharge_range_end',
    'roll_type_adv', 'roll_type_dis', 'roll_type_against_dis', 'extra_attack', 'ap_change_during_turn',
    'effect_ends_on_start_of_caster_next_turn', 'status_effect_applied_to_target_after_cast',
    'triggers_on_roll_type_against_the_caster',
    // Boundary AP, equipment speed and disarm defence have no outgoing-swing duty.
    'if_caster_has_status_effect_active', 'ap_change_at_start_of_turn', 'speed_climbing', 'immune_to_disarm']);
const SEPARATE_GRANTS = new Set([...WEAPON_CONTEXT].filter(key => key.endsWith('_ability') || key.endsWith('_spell')));
const WEAPON_OUTCOME_KEYS = new Set(['triggers_on_hit', 'triggers_on_miss', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit']);
SEPARATE_GRANTS.add('innate_spell_per_long_rest');
SEPARATE_GRANTS.add('ability');
type EnchantmentEntry = { bucket: string; entry: Row; definition: Row };
function weaponAbilityDefinition(raw: unknown, owner: Row, read: ExplicitWeaponReaders): Row | null {
    const entry = record(raw), nested = record(entry?.item_arr) ?? entry;
    if (nested && record(nested.item_layer_types)) return nested;
    const id = layerListEntryUuid(entry?.item_arr ?? raw);
    const candidate = record(record(owner.catalog)?.[id]);
    return candidate && (record(candidate.item_layer_types) || record(candidate.computed)) ? candidate : read.resolveItem?.(id) ?? null;
}

/** C2 full nests and lean {item_id, source, built_from} entries are the same grant.
 * Resolve through the page's catalog reader; never require an obsolete item_arr. */
function enchantmentEntries(weapon: Row, read: ExplicitWeaponReaders): EnchantmentEntry[] | { reason: string } {
    const raw = read.computedValue(weapon, 'enchantment', 'abilities');
    if (raw == null) return [];
    if (Array.isArray(raw) && raw.length === 0) return [];
    const buckets = record(raw);
    if (!buckets) return { reason: 'weapon:family:enchantment.abilities:unreadable' };
    const out: EnchantmentEntry[] = [];
    for (const [bucket, entries] of Object.entries(buckets)) {
        if (!Array.isArray(entries)) return { reason: 'weapon:family:enchantment.abilities:' + bucket + ':unreadable' };
        for (const rawEntry of entries) {
            const entry = record(rawEntry), nested = record(entry?.item_arr) ?? entry;
            const id = layerListEntryUuid(nested);
            const definition = weaponAbilityDefinition(rawEntry, weapon, read);
            if (!entry || !definition) return { reason: 'weapon:family:enchantment.abilities:' + String(read.itemField(nested, 'item_name') ?? id ?? bucket) + ':unavailable' };
            out.push({ bucket, entry, definition });
        }
    }
    return out;
}

const damagePassive = (definition: Row, read: ExplicitWeaponReaders): boolean =>
    [EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL].includes(upper(read.value(definition, 'roll_type')));
function enchantmentRefusal({ bucket, definition }: EnchantmentEntry, weapon: Row, caster: Row, read: ExplicitWeaponReaders): string | null {
    const name = String(read.itemField(definition, 'item_name') ?? read.itemField(definition, 'item_id') ?? bucket);
    const no = (duty: string): string => 'weapon:family:enchantment.abilities:' + name + ':' + duty;
    const keys = ownLayerKeys(definition).filter(key => read.entries(definition, key).length > 0);
    if (bucket !== 'passive_ability') {
        if (!SEPARATE_GRANTS.has(bucket)) return no(bucket);
        if (!TRIGGER_CARRIERS.some(carrier => carrier === bucket)) return null;
        // Ordinary grants remain separate actions. A matching automatic listener is
        // due during the swing and needs an executor, rather than disappearing.
        const triggers = keys.filter(key => key.includes('trigger'));
        if (!triggers.length) return null;
        // A stationary swing does not emit a creature entering-reach event.
        if (triggers.every(key => key === 'triggers_when_target_enters_reach')) return null;
        const rollTypes = read.entries(definition, 'triggers_when_caster_makes_roll_type').map(layerListEntryUuid);
        if (triggers.every(key => key === 'triggers_when_caster_makes_roll_type')
            && rollTypes.length && !rollTypes.includes(upper(read.value(weapon, 'roll_type')))) return null;
        return no('trigger');
    }
    const meets = skillRequirementsMet(definition, caster, read);
    if (meets === null) return no('skill-catalog-unavailable');
    if (!meets) return null;
    const supported = damagePassive(definition, read) ? FRAGMENT_KNOWN_LAYERS
        : new Set([...COMPILED_ENCHANTMENT, 'dice', 'die', 'damage_dice_quantity_mod', 'ignore_damage_type_resistance']);
    const unsupported = keys.find(key => !supported.has(key) && !key.startsWith('requires_skill_tier_'));
    if (unsupported) return no(unsupported);
    for (const key of ['dice', 'die', 'damage_dice_quantity_mod', 'weapon_attack_mod']) {
        const value = read.value(definition, key);
        if (value != null && !Number.isInteger(Number(value))) return no(key + ':unreadable');
    }
    return null;
}

/** Materialize only the striking weapon's instance effects, on a transient clone.
 * Size parts override dice/die or add dice; +N contributes to-hit independently
 * of its extra-damage fragment (boon_coating_helpers.php's enchantment bridge). */
export function weaponEnchantmentAttackLayers(weapon: Row, read: ExplicitWeaponReaders, additional?: readonly Row[]): { weapon: Row } | { reason: string } {
    const entries = additional ? additional.map(definition => ({ bucket: 'passive_ability', definition })) : enchantmentEntries(weapon, read);
    if (!Array.isArray(entries)) return entries;
    const layers = { ...record(weapon.item_layer_types) };
    let dice = Number(read.value(weapon, 'dice')), die = Number(read.value(weapon, 'die'));
    let diceMod = 0, attackMod = Number(read.value(weapon, 'weapon_attack_mod') ?? 0);
    const ignoredResistance = [...read.entries(weapon, 'ignore_damage_type_resistance')];
    const rollAdv = [...read.entries(weapon, 'roll_type_adv')];
    for (const { bucket, definition } of entries) {
        attackMod += Number(read.value(definition, 'weapon_attack_mod') ?? 0);
        if (bucket !== 'passive_ability' || read.value(definition, 'roll_type') != null) continue;
        const partDice = read.value(definition, 'dice'), partDie = read.value(definition, 'die');
        if (partDice != null) dice = Number(partDice);
        if (partDie != null) die = Number(partDie);
        diceMod += Number(read.value(definition, 'damage_dice_quantity_mod') ?? 0);
        ignoredResistance.push(...read.entries(definition, 'ignore_damage_type_resistance'));
        rollAdv.push(...read.entries(definition, 'roll_type_adv'));
        const composition = read.value(definition, 'item_composition');
        if (composition != null) layers.item_composition = composition;
    }
    if (entries.length) { layers.dice = dice + diceMod; layers.die = die; }
    if (attackMod) layers.weapon_attack_mod = attackMod;
    if (ignoredResistance.length) layers.ignore_damage_type_resistance = ignoredResistance;
    if (rollAdv.length) layers.roll_type_adv = rollAdv;
    return { weapon: entries.length ? { ...weapon, item_layer_types: layers } : weapon };
}

/** The browser damage/trigger executor consumes creature passives. Restore lean
 * grants and supplement only missing weapon grants, preserving active stamps
 * and avoiding a second roll when C2 already surfaced the enchantment. */
export function explicitWeaponPassiveEntries(weapon: Row, caster: Row, read: ExplicitWeaponReaders): unknown[] {
    const entries = read.entries(caster, 'passive_ability').map(raw => {
        const entry = record(raw), definition = weaponAbilityDefinition(raw, caster, read);
        return entry && definition ? { ...entry, item_arr: definition,
            ...(skillRequirementsMet(definition, caster, read) === false ? { passive_conditions_active: false } : {}) } : raw;
    });
    const ids = new Set(entries.map(raw => layerListEntryUuid(record(raw)?.item_arr ?? raw)));
    for (const raw of read.entries(weapon, 'passive_ability')) {
        const definition = weaponAbilityDefinition(raw, weapon, read);
        const id = upper(read.itemField(definition, 'item_id'));
        if (!definition || ids.has(id) || skillRequirementsMet(definition, caster, read) !== true) continue;
        entries.push({ ...record(raw), item_arr: definition }); ids.add(id);
    }
    const enchantments = enchantmentEntries(weapon, read);
    if (!Array.isArray(enchantments)) return entries; // the family gate reports the malformed grant before dice
    for (const { bucket, entry, definition } of enchantments) {
        const id = upper(read.itemField(definition, 'item_id'));
        if (bucket !== 'passive_ability' || !damagePassive(definition, read) || ids.has(id)
            || skillRequirementsMet(definition, caster, read) !== true) continue;
        entries.push({ ...entry, item_arr: definition }); ids.add(id);
    }
    return entries;
}

/** Keep the weapon, modifier and consequence readers on the same eligible live
 * grants. Frame clones receive their own entries, including their charge state. */
export function explicitWeaponAttackReaders(weapon: Row, caster: Row, read: ExplicitWeaponReaders): ExplicitWeaponReaders {
    const cache = new WeakMap<Row, unknown[]>();
    const actorId = read.creatureField?.(caster, 'encounter_creature_id');
    const isCaster = (row: Row | null): row is Row => !!row && (row === caster
        || !!actorId && read.creatureField?.(row, 'encounter_creature_id') === actorId);
    const passives = (row: Row): unknown[] => {
        let entries = cache.get(row);
        if (!entries) { entries = explicitWeaponPassiveEntries(weapon, row, read); cache.set(row, entries); }
        return entries;
    };
    return { ...read,
        entries: (row, key) => isCaster(row) && key === 'passive_ability' ? passives(row) : read.entries(row, key),
        value: (row, key) => {
            if (isCaster(row) && key === 'ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach') {
                const sources = passives(row).map(record).filter((entry): entry is Row => !!entry)
                    .filter(entry => read.entries(record(entry.item_arr), key).length > 0);
                if (sources.length) return sources.some(entry => passiveActive(read, entry)) ? 1 : null;
            }
            return read.value(row, key);
        } };
}
function skillRequirementsMet(ability: Row, caster: Row, read: ExplicitWeaponReaders): boolean | null {
    if (!ownLayerKeys(ability).some(key => key.startsWith('requires_skill_tier_'))) return true;
    if (!read.skillEntries) return null;
    return createSkillRequirementChecker({ skillEntries: read.skillEntries, layerEntries: (row, key) => read.entries(row as Row | null, key),
        catalogRows: {}, abilityTypes: [], aggregationStrategy: () => 'list', resolveCarrierRef: () => null, resolveInventoryInstance: () => null })(ability, caster);
}
/** Bucketed + legacy flat layer names; row structure and `_`-prefixed object-API metadata (`_pl_stash` on a definition
 * hydrated through object_inc.php) are never layers — execrhythm28b. One enumeration, shared with the budget and fragment gates. */
const ownLayerKeys = rowLayerNames;

/** Inspect keys on the held template and classification too: lean inventory rows
 * omit inherited layers. Values are always read through the supplied page reader. */
function weaponLayerKeys(weapon: Row, read: ExplicitWeaponReaders): string[] {
    const keys = new Set(ownLayerKeys(weapon));
    const ids = [read.inventoryField(weapon, 'inventory_item_id'), read.itemField(weapon, 'item_id'), read.value(weapon, 'weapon_classification')];
    for (const raw of ids) {
        const id = upper(raw);
        const definition = record(record(weapon.catalog)?.[id]) ?? read.resolveItem?.(id);
        if (definition) for (const key of ownLayerKeys(definition)) keys.add(key);
    }
    return [...keys].filter(key => read.entries(weapon, key).length > 0);
}

/** Incoming-only listeners and other roll types owe no outgoing-swing payload.
 * Outcomes are still unknown before dice, so admit every possible hit/miss/crit. */
function weaponPassiveCouldFire(def: Row, weapon: Row, read: ExplicitWeaponReaders, throwing: boolean): boolean {
    const families = [...new Set(ownLayerKeys(def).filter(key => key.startsWith('triggers_') && !WEAPON_OUTCOME_KEYS.has(key) && read.entries(def, key).length))];
    if (families.length !== 1) return true;
    if (families[0] === 'triggers_on_roll_type_against_the_caster') return false;
    if (families[0] !== 'triggers_when_caster_makes_roll_type') return true;
    const rollType = weaponAttackRollType({ throwWeapon: throwing }, weapon, read);
    return [false, true].some(hit => [false, true].some(criticalHit => predicateMatch(read,
        { trigger: 'triggers_when_caster_makes_roll_type', rollType, hit, criticalHit }, def)));
}

/** Unsupported explicit duties must be identified before entering the dice lane. */
export function explicitWeaponFamilyRefusal(binding: Exclude<ExplicitWeaponBinding, { reason: string }>, caster: Row, read: ExplicitWeaponReaders,
    throwing = false): string | null {
    const { weapon, ability } = binding;
    throwing ||= read.entries(ability, 'attack_ability_throws_the_throwing_weapon_at_target').length > 0;
    // Legacy janus() requires an authored range_end; it invents no improvised throw range.
    if (throwing && !(Number(read.value(weapon, 'range_end')) > 0)) return WEAPON_CANNOT_BE_THROWN;
    if (throwing && read.entries(caster, 'throwing_distance_changed_by_percentage').length) return 'weapon:family:throwing_distance_changed_by_percentage';
    if (!['2FE8DFB7-FE61-4862-AF71-3523262350A0', '3F85CA97-F9B2-A83A-FDE8-E523FD99346C'].includes(upper(read.value(weapon, 'roll_type')))) return 'weapon:family:roll_type';
    if (read.entries(caster, 'weapon_essence').length) return 'weapon:family:weapon_essence';
    if (read.entries(weapon, 'versatile_dice').length && upper(read.inventoryField(weapon, 'equipped')) === THROW_HAND_SLOTS.both_hands)
        return 'weapon:family:versatile-two-handed';
    const passives = explicitWeaponPassiveEntries(weapon, caster, read);
    for (const entry of read.entries(weapon, 'passive_ability')) {
        const def = weaponAbilityDefinition(entry, weapon, read);
        if (!def) return 'weapon:family:weapon-passive-unavailable';
        if (skillRequirementsMet(def, caster, read) === false) continue;
        const live = record(passives.find(raw => layerListEntryUuid(record(raw)?.item_arr ?? raw) === upper(read.itemField(def, 'item_id'))));
        if ((live && !passiveActive(read, live)) || !weaponPassiveCouldFire(def, weapon, read, throwing)) continue;
        if (live && read.entries(def, 'ability_charges').length) {
            const pool = entryCharges(live, upper(read.itemField(def, 'item_id')), { value: read.value, resolveItem: () => def });
            if (pool?.currentHeld && pool.current !== null && pool.current !== '' && Number(pool.current) <= 0
                && chargeReductionDelta(caster, -1, weaponReductionReaders(read)) < 0) continue;
        }
        const supported = damagePassive(def, read) ? FRAGMENT_KNOWN_LAYERS : WEAPON_PASSIVE;
        const transientAttackGrant = read.entries(def, 'extra_attack').length > 0
            && ownLayerKeys(def).some(key => key.includes('trigger')) && !isAttackPassiveWindow(def);
        const unsupportedStatus = read.entries(def, 'status_effect_applied_to_target_after_cast').some(raw => {
            const status = weaponAbilityDefinition(raw, weapon, read);
            return !status ? !read.deferStatusDefinitions
                : ownLayerKeys(status).some(key => read.entries(status, key).length && !statusLiveLayer(key));
        });
        if (transientAttackGrant || unsupportedStatus || ownLayerKeys(def).some(key => !supported.has(key) && !key.startsWith('requires_skill_tier_') && read.entries(def, key).length))
            return 'weapon:family:weapon-passive:' + String(read.itemField(def, 'item_name') ?? read.itemField(def, 'item_id'));
    }
    const enchantments = enchantmentEntries(weapon, read);
    if (!Array.isArray(enchantments)) return enchantments.reason;
    const selectedGrant = enchantments.find(entry => upper(read.itemField(entry.definition, 'item_id')) === upper(read.itemField(ability, 'item_id')));
    const abilityReason = (reason: string): string => selectedGrant ? reason + ':' + String(read.itemField(selectedGrant.definition, 'item_name')) : reason;
    const teleportChoice = attackGrantsTeleportChoice(ability, read);
    // Player attacks run the rider-first ability gate before committing. Direct opportunity attacks do not;
    // retain their existing family refusal until that lane enforces the same precondition.
    const effectKeys = ownLayerKeys(ability).filter(key => (!ATTACK_CONTEXT.has(key)
        || (key === 'if_caster_has_status_effect_active' && read.itemType(ability) !== 'attack_ability'))
        && !(key === 'caster_dive_bombs_target' && read.entries(ability, 'caster_moves_towards_target_in_ft').length > 0)
        && !(key === 'mod' && read.entries(ability, 'add_charges_to_ability').length > 0)
        && !(teleportChoice && ['caster_moves_to_end_point_of_aoe_effect_after_cast', 'caster_moves_towards_target_in_ft', 'effect_requires_preferred_speed_type'].includes(key))
        && !key.startsWith('requires_skill_tier_') && read.entries(ability, key).length > 0);
    // Keep an unported combination's existing diagnostic intact as its supported
    // pieces acquire executors. No new refusal name for the remainder.
    const familyReason = (): string => abilityReason('weapon:family:' + [...new Set(effectKeys)].sort().join(','));
    if (effectKeys.some(key => key !== 'caster_moves_towards_target_in_ft'
        && !HIT_EFFECTS.has(key) && !(key === 'roll_type' && HIT_ROLLS.has(upper(read.value(ability, key))))))
        return familyReason();
    // The bound click has no amount/damage-type picker yet. Preserve the
    // existing family refusal for variable dice instead of dropping the choice.
    if (read.entries(ability, 'dice').length && !/^\d+$/.test(String(read.value(ability, 'dice')))) return familyReason();
    if (Number(read.value(ability, 'max_targets') ?? 1) !== 1) return effectKeys.length ? familyReason() : abilityReason('weapon:family:max_targets');
    // Reuse the compiler's capability boundary for authored rider payloads too.
    // A family with unported status machinery keeps its original refusal.
    for (const key of ['status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_caster_on_hit']) {
        for (const entry of read.entries(ability, key)) {
            const definition = read.resolveItem?.(layerListEntryUuid(entry));
            if (definition && Object.keys(record(definition.item_layer_types) ?? {}).some(layer => !statusLiveLayer(layer))) return familyReason();
        }
    }
    if (read.entries(weapon, 'layer_on_status_effects').length) return 'weapon:family:layer_on_status_effects';
    for (const entry of enchantments) {
        const reason = enchantmentRefusal(entry, weapon, caster, read);
        if (reason) return reason;
    }
    const weaponFamilies = weaponLayerKeys(weapon, read).filter(key => !WEAPON_CONTEXT.has(key));
    if (weaponFamilies.length) return 'weapon:family:' + weaponFamilies.sort().join(',');
    return null;
}

/** Charge preflight operates on a clone: preparation never debits the page row.
 * Recharge remains the existing start-of-turn charge lane, not another attack roll. */
export function explicitWeaponChargeContext(ability: Row, caster: Row, encounter: Row, read: ExplicitWeaponReaders, roll: RollSource,
    inCombat: boolean): JanusCtx {
    const clone = JSON.parse(JSON.stringify(caster)) as Row;
    const unavailable = (): never => handBack('weapon:charge-reader-unavailable');
    const meets = skillRequirementsMet(ability, caster, read);
    if (meets === null) handBack('weapon:skill-catalog-unavailable');
    if (!meets) handBack('weapon:skill-requirement');
    const readers: JanusReaders = {
        value: read.value, entries: read.entries, has: (row, key) => read.entries(row, key).length > 0,
        definition: id => upper(read.itemField(ability, 'item_id')) === upper(id) ? ability : read.resolveItem?.(id) ?? null,
        statuses: weaponStatusRows,
        inCombat: () => inCombat, inventoryField: read.inventoryField,
        visibility: unavailable, reactionInventoryComplete: unavailable, deadState: unavailable,
        campaignClock: unavailable, addCampaignSeconds: unavailable, uuid: unavailable,
    };
    const ctx: JanusCtx = { encounter, members: new Map([['caster', clone]]), originals: new Map(), read: readers, roll,
        rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: null, rollPrivate: 0 };
    const id = upper(read.itemField(ability, 'item_id'));
    if (readers.has(ability, 'ability_charges')) {
        const pool = abilityChargeEntry(ctx, clone, id);
        if ((inCombat || !(readers.has(ability, 'recharge_range_begin') && readers.has(ability, 'recharge_range_end'))) && pool.current < 1
            && chargeReductionDelta(clone, -1, resourceReductionReaders(ctx)) < 0)
            handBack('weapon:ability-charges-empty');
    }
    preflightPostRollChargeResources(ctx, ability, clone);
    return ctx;
}

export function spendExplicitWeaponCharges(ctx: JanusCtx, ability: Row, caster: Row = ctx.members.get('caster')!, hit?: boolean): JanusCtx['chargesChanged'] {
    janusPostRollChangeAbilityResources(ctx, ability, caster, null, hit);
    return ctx.chargesChanged;
}

export function explicitWeaponSizeAllowed(ability: Row, caster: Row, target: Row, read: ExplicitWeaponReaders): boolean {
    return checkAbilitySizeCompatibility({ read: { value: read.value } } as JanusCtx, ability, caster, target);
}
